import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequestOptions, getAxiosConfig } from '../helpers/Fetchwrapper';
import { debounce } from 'lodash';

const PurchaseOrderForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    currency: 'USD',
    notes: '',
    terms_conditions: '',
    tax_rate: 0,
    discount_amount: 0,
    shipping_amount: 0
  });

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errors, setErrors] = useState({});
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    if (isEditing) {
      fetchPurchaseOrder();
    }
  }, [id]);

  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      searchProducts();
    } else if (searchTerm.length === 0) {
      setProducts([]);
    }
  }, [searchTerm]);

  // Debug useEffect to monitor items state
  useEffect(() => {
    console.log('Items state updated:', items);
  }, [items]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + 'suppliers', getRequestOptions());
      const data = await response.json();
      
      if (data.success) {
        setSuppliers(data.data.data || []);
      } else {
        toast.error('Error fetching suppliers');
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Error fetching suppliers');
    }
  };

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_API_URL + `purchase-orders/${id}`, getRequestOptions());
      const data = await response.json();
      
      if (data.success) {
        const po = data.data;
        
        setFormData({
          supplier_id: po.supplier_id,
          order_date: po.order_date,
          expected_delivery_date: po.expected_delivery_date,
          currency: po.currency,
          notes: po.notes || '',
          terms_conditions: po.terms_conditions || '',
          tax_rate: 0,
          discount_amount: po.discount_amount || 0,
          shipping_amount: po.shipping_amount || 0
        });
        
        setItems(po.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          quantity_ordered: item.quantity_ordered,
          unit_price: item.unit_price,
          total_price: item.total_price
        })));
      } else {
        toast.error('Error fetching purchase order');
        history.push('/purchase-orders');
      }
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      toast.error('Error fetching purchase order');
      history.push('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = debounce(async () => {
    try {
      setProductLoading(true);
      
      const params = new URLSearchParams({
        search: searchTerm,
        limit: '10',
        globalSearch: searchTerm
      });

      // Try different product endpoints
      let response;
      let data;
      
      try {
        // First try the products endpoint
        response = await fetch(process.env.REACT_APP_API_URL + `products?${params.toString()}`, getRequestOptions());
        data = await response.json();
      } catch (error) {
        // If that fails, try products_list endpoint
        response = await fetch(process.env.REACT_APP_API_URL + `products_list?${params.toString()}`, getRequestOptions());
        data = await response.json();
      }

      if (data.success) {
        // Handle different response structures
        let productData = [];
        if (data.data) {
          if (Array.isArray(data.data)) {
            productData = data.data;
          } else if (data.data.data) {
            productData = data.data.data;
          } else {
            productData = data.data;
          }
        } else if (Array.isArray(data)) {
          productData = data;
        }

        setProducts(productData);
      } else {
        console.error('Failed to fetch products:', data.errorMessage || data.message);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Error searching products');
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  }, 500);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAddItem = () => {
    if (!selectedProduct) return;

    const existingItem = items.find(item => item.product_id === selectedProduct.product_id);
    if (existingItem) {
      toast.warning('Product already added to the order');
      return;
    }

    // Handle different product field names
    const productName = selectedProduct.name || selectedProduct.product_name || selectedProduct.productName;
    const productSku = selectedProduct.sku || selectedProduct.product_sku;
    const productId = selectedProduct.product_id || selectedProduct.id;
    const costPrice = parseFloat(selectedProduct.cost_price || selectedProduct.price || 0);

    const newItem = {
      product_id: productId,
      product_name: productName,
      product_sku: productSku,
      quantity_ordered: 1,
      unit_price: costPrice,
      total_price: costPrice
    };

    setItems(prev => [...prev, newItem]);
    setSelectedProduct(null);
    setShowProductModal(false);
    setSearchTerm('');
  };

  const handleSelectProduct = (product) => {
    const productId = product.product_id || product.id;
    console.log('Selecting product:', product.name, 'ID:', productId);
    
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => (p.product_id || p.id) === productId);
      console.log('Is product already selected:', isSelected);
      
      if (isSelected) {
        const filtered = prev.filter(p => (p.product_id || p.id) !== productId);
        console.log('Removing product, new selection:', filtered);
        return filtered;
      } else {
        const updated = [...prev, product];
        console.log('Adding product, new selection:', updated);
        return updated;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
      setSelectAll(false);
    } else {
      setSelectedProducts([...products]);
      setSelectAll(true);
    }
  };

  const handleBulkAdd = () => {
    if (selectedProducts.length === 0) {
      toast.warning('Please select at least one product');
      return;
    }

    console.log('Selected products:', selectedProducts);
    console.log('Current items:', items);

    const newItems = [];
    const existingProductIds = items.map(item => item.product_id);

    selectedProducts.forEach(product => {
      const productId = product.product_id || product.id;
      console.log('Processing product:', product.name, 'ID:', productId);
      
      if (existingProductIds.includes(productId)) {
        toast.warning(`${product.name || product.product_name} is already in the order`);
        return;
      }

      const productName = product.name || product.product_name || product.productName;
      const productSku = product.sku || product.product_sku;
      const costPrice = parseFloat(product.cost_price || product.price || 0);

      const newItem = {
        product_id: productId,
        product_name: productName,
        product_sku: productSku,
        quantity_ordered: 1,
        unit_price: costPrice,
        total_price: costPrice
      };

      console.log('Adding new item:', newItem);
      newItems.push(newItem);
    });

    console.log('New items to add:', newItems);

    if (newItems.length > 0) {
      // Use a more direct approach to update state
      const currentItems = [...items];
      const updatedItems = [...currentItems, ...newItems];
      
      console.log('Current items before update:', currentItems);
      console.log('Updated items after adding:', updatedItems);
      
      setItems(updatedItems);
      
      // Force a re-render by updating a dummy state
      setTimeout(() => {
        console.log('Items state after timeout:', items);
        toast.success(`${newItems.length} product(s) added to order`);
      }, 100);
    } else {
      toast.warning('No new products to add (all selected products are already in the order)');
    }

    // Reset modal state
    setSelectedProducts([]);
    setSelectAll(false);
    setShowProductModal(false);
    setSearchTerm('');
  };

  const isProductSelected = (product) => {
    const productId = product.product_id || product.id;
    return selectedProducts.some(p => (p.product_id || p.id) === productId);
  };

  // Test function to manually add items
  const testAddItems = () => {
    const testItem = {
      product_id: 'test-' + Date.now(),
      product_name: 'Test Product',
      product_sku: 'TEST-SKU',
      quantity_ordered: 1,
      unit_price: 10.00,
      total_price: 10.00
    };
    
    console.log('Adding test item:', testItem);
    setItems(prev => {
      const updated = [...prev, testItem];
      console.log('Updated items with test:', updated);
      return updated;
    });
    toast.success('Test item added');
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    
    if (field === 'quantity_ordered') {
      updatedItems[index][field] = parseInt(value) || 0;
    } else if (field === 'unit_price') {
      updatedItems[index][field] = parseFloat(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }
    
    if (field === 'quantity_ordered' || field === 'unit_price') {
      const quantity = parseInt(updatedItems[index].quantity_ordered) || 0;
      const unitPrice = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total_price = quantity * unitPrice;
    }
    
    setItems(updatedItems);
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.total_price) || 0;
      return sum + itemTotal;
    }, 0);
    const taxRate = parseFloat(formData.tax_rate) || 0;
    const taxAmount = (taxRate / 100) * subtotal;
    const discountAmount = parseFloat(formData.discount_amount) || 0;
    const shippingAmount = parseFloat(formData.shipping_amount) || 0;
    const total = subtotal + taxAmount - discountAmount + shippingAmount;
    
    return { subtotal, taxAmount, total };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'Supplier is required';
    }
    if (!formData.order_date) {
      newErrors.order_date = 'Order date is required';
    }
    if (!formData.expected_delivery_date) {
      newErrors.expected_delivery_date = 'Expected delivery date is required';
    }
    if (new Date(formData.expected_delivery_date) <= new Date(formData.order_date)) {
      newErrors.expected_delivery_date = 'Expected delivery date must be after order date';
    }
    if (items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const { subtotal, taxAmount, total } = calculateTotals();
      
      const payload = {
        ...formData,
        items: items.map(item => ({
          product_id: item.product_id,
          quantity_ordered: item.quantity_ordered,
          unit_price: item.unit_price
        })),
        tax_amount: taxAmount,
        subtotal,
        total_amount: total
      };

      const requestOptions = {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json', 
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('MyToken'))
        },
        body: JSON.stringify(payload)
      };

      const url = isEditing 
        ? process.env.REACT_APP_API_URL + `purchase-orders/${id}`
        : process.env.REACT_APP_API_URL + 'purchase-orders';

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.success) {
        toast.success(isEditing ? 'Purchase order updated successfully' : 'Purchase order created successfully');
        history.push('/purchase-orders');
      } else {
        const errorMessage = data.message || 'Error saving purchase order';
        toast.error(errorMessage);
        
        if (data.errors) {
          setErrors(data.errors);
        }
      }
    } catch (error) {
      console.error('Error saving purchase order:', error);
      toast.error('Error saving purchase order');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  if (loading && isEditing) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <div className="purchase-order-form">
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">
            {isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </h4>
        </div>
        <div className="card-body">
          <form>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Supplier *</label>
                  <select
                    className={`form-select ${errors.supplier_id ? 'is-invalid' : ''}`}
                    value={formData.supplier_id}
                    onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.supplier_id} value={supplier.supplier_id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                  {errors.supplier_id && <div className="invalid-feedback">{errors.supplier_id}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-select"
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Order Date *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.order_date ? 'is-invalid' : ''}`}
                    value={formData.order_date}
                    onChange={(e) => handleInputChange('order_date', e.target.value)}
                  />
                  {errors.order_date && <div className="invalid-feedback">{errors.order_date}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Expected Delivery Date *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.expected_delivery_date ? 'is-invalid' : ''}`}
                    value={formData.expected_delivery_date}
                    onChange={(e) => handleInputChange('expected_delivery_date', e.target.value)}
                  />
                  {errors.expected_delivery_date && <div className="invalid-feedback">{errors.expected_delivery_date}</div>}
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Order Items</h5>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowProductModal(true)}
                >
                  Add Item
                </button>
              </div>

              {errors.items && (
                <div className="alert alert-danger">{errors.items}</div>
              )}

              {items.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.product_name}</td>
                          <td>{item.product_sku}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              className="form-control"
                              style={{ width: '80px' }}
                              value={item.quantity_ordered}
                              onChange={(e) => handleItemChange(index, 'quantity_ordered', parseInt(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              style={{ width: '100px' }}
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                            />
                          </td>
                          <td>${(parseFloat(item.total_price) || 0).toFixed(2)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveItem(index)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals Section */}
              {items.length > 0 && (
                <div className="row mt-3">
                  <div className="col-md-6"></div>
                  <div className="col-md-6">
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td><strong>Subtotal:</strong></td>
                          <td className="text-end">${(parseFloat(subtotal) || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Tax Rate (%):</strong>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="form-control d-inline-block"
                              style={{ width: '80px', marginLeft: '10px' }}
                              value={formData.tax_rate}
                              onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value))}
                            />
                          </td>
                          <td className="text-end">${(parseFloat(taxAmount) || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Discount Amount:</strong>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control d-inline-block"
                              style={{ width: '100px', marginLeft: '10px' }}
                              value={formData.discount_amount}
                              onChange={(e) => handleInputChange('discount_amount', parseFloat(e.target.value))}
                            />
                          </td>
                          <td className="text-end">-${(parseFloat(formData.discount_amount) || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Shipping Amount:</strong>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control d-inline-block"
                              style={{ width: '100px', marginLeft: '10px' }}
                              value={formData.shipping_amount}
                              onChange={(e) => handleInputChange('shipping_amount', parseFloat(e.target.value))}
                            />
                          </td>
                          <td className="text-end">${(parseFloat(formData.shipping_amount) || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td><strong>Total:</strong></td>
                          <td className="text-end"><strong>${(parseFloat(total) || 0).toFixed(2)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="row mt-4">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Terms & Conditions</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.terms_conditions}
                    onChange={(e) => handleInputChange('terms_conditions', e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => history.push('/purchase-orders')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className='modal show' style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060}} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Product</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowProductModal(false);
                    setSearchTerm('');
                    setProducts([]);
                    setSelectedProduct(null);
                    setSelectedProducts([]);
                    setSelectAll(false);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Search Products</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or SKU (minimum 2 characters)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {productLoading && (
                    <div className="mt-2">
                      <small className="text-muted">Searching products...</small>
                    </div>
                  )}
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {products.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                />
                                <label className="form-check-label">
                                  All
                                </label>
                              </div>
                            </th>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Price</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(product => {
                            const productName = product.name || product.product_name || product.productName;
                            const productSku = product.sku || product.product_sku;
                            const productPrice = product.cost_price || product.price || 0;
                            const isSelected = isProductSelected(product);
                            
                            return (
                              <tr key={product.product_id || product.id} className={isSelected ? 'table-primary' : ''}>
                                <td>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleSelectProduct(product)}
                                    />
                                  </div>
                                </td>
                                <td>{productName}</td>
                                <td>{productSku}</td>
                                <td>${productPrice}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setSelectedProduct(product)}
                                  >
                                    Select
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : searchTerm.length >= 2 && !productLoading ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No products found</p>
                    </div>
                  ) : searchTerm.length < 2 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">Enter at least 2 characters to search</p>
                    </div>
                  ) : null}
                </div>

                {selectedProducts.length > 0 && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">
                        {selectedProducts.length} product(s) selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={handleBulkAdd}
                      >
                        Add Selected ({selectedProducts.length})
                      </button>
                    </div>
                    {/* Debug info */}
                    <div className="mt-2">
                      <small className="text-muted">
                        Selected: {selectedProducts.map(p => p.name || p.product_name).join(', ')}
                      </small>
                    </div>
                    <div className="mt-1">
                      <small className="text-muted">
                        Current items in order: {items.length}
                      </small>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={handleAddItem}
                      disabled={!selectedProduct}
                    >
                      Add Single Product
                    </button>
                    {selectedProducts.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-success me-2"
                        onClick={handleBulkAdd}
                      >
                        Add Selected ({selectedProducts.length})
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={testAddItems}
                    >
                      Test Add
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowProductModal(false);
                      setSearchTerm('');
                      setProducts([]);
                      setSelectedProduct(null);
                      setSelectedProducts([]);
                      setSelectAll(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderForm; 